import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header';
import { api } from '../../api/client';
import { useOfflineQuery } from '../../lib/offline/use-offline-query';
import { jobStatusLabel, jobStatusStyle } from '../../lib/job-status';
import { formatCalendarDate, formatScheduledTime } from '../../lib/calendar-date';
import {
  ArrowLeft,
  MapPin,
  ChevronRight,
  Calendar,
  Search
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';
import { useUrlState } from '../../hooks/useUrlState';
import { rankFieldJobs } from '../../lib/field-job-order';
import { PHASE_WORD, handPhases, phaseDateValue, phaseMismatch, type FieldPhase } from '../../lib/job-phase-dates';

/**
 * The phases a row dates, each with its raw wall-clock value. A single-phase
 * hand on a job without that phase still gets their own phase, em-dashed,
 * never the other phase's date (lib/job-phase-dates.ts — no fallback).
 */
function fieldJobPhaseDates(job: any): { phase: FieldPhase; value: string | null }[] {
  const mismatch = phaseMismatch(job, job?.my_phase);
  const phases: FieldPhase[] = mismatch ? [mismatch] : handPhases(job, job?.my_phase);
  return phases.map((phase) => ({ phase, value: phaseDateValue(job, phase) }));
}

// Order only — filter behaviour is unchanged. `all` sits last to match My Tickets.
const STATUS_TABS = ['scheduled', 'active', 'completed', 'all'] as const;

export default function FieldJobsScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/field');
  const location = useLocation();
  // Tab and search live in the URL (useUrlState), so Back from a job returns to
  // the same list. A tab handed over in router state is only the default.
  const initialTab = (location.state as any)?.tab || 'all';
  const [activeTab, setActiveTab] = useUrlState<string>('tab', initialTab);
  const [search, setSearch] = useUrlState<string>('q', '');

  const params = activeTab === 'all' ? {} : { status: activeTab };
  const { data, isLoading } = useOfflineQuery(
    'jobs',
    ['field-my-jobs', activeTab],
    () => api.employee.myJobs(params).then(r => r.data.data ?? r.data ?? []),
    { filterFn: activeTab === 'all' ? undefined : (j: any) => j.status === activeTab }
  );

  const jobs: any[] = Array.isArray(data) ? rankFieldJobs(data) : [];
  const filtered = search
    ? jobs.filter(j =>
        j.job_number?.toLowerCase().includes(search.toLowerCase()) ||
        j.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        j.rig?.toLowerCase().includes(search.toLowerCase())
      )
    : jobs;

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Jobs</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
          <input
            type="text"
            placeholder="Search jobs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-fieldiq-card rounded-xl pl-10 pr-4 py-3 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
          />
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card text-fieldiq-text'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Jobs List */}
        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading jobs...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-fieldiq-text">
            {search ? 'No jobs match your search' : 'No jobs found'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((job: any) => (
              <button
                key={job.id}
                onClick={() => navigate(`/field/jobs/${job.id}`)}
                className="w-full bg-fieldiq-card rounded-xl p-4 text-left card-hover"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{job.job_number}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${jobStatusStyle(job.status)}`}>
                        {jobStatusLabel(job.status)}
                      </span>
                    </div>
                    <p className="text-sm text-fieldiq-text">{job.customer_name}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-fieldiq-muted">
                      {/*
                        This printed the raw column — '2026-09-10T12:00:00.000Z'
                        straight onto a phone. Not a formatting nit: it is the
                        one field on this card a hand reads to know which day
                        they are working, and it was showing them a UTC
                        timestamp. Absent rendered as nothing at all, so a job
                        with no date looked the same as one whose date failed to
                        load.
                      */}
                      {/*
                        The date of the phase(s) THIS hand works (my_phase,
                        api TNT J-4404): a wash-only hand sees the wash date, a
                        run-only hand the run date, both/NULL see both,
                        labelled. It printed scheduled_date (the run) for
                        everyone, so a wash hand read the run's day as theirs.
                      */}
                      {fieldJobPhaseDates(job).map(({ phase, value }) => (
                        <span key={phase} className="flex items-center gap-1" data-testid={`job-${job.id}-${phase}-date`}>
                          <Calendar size={12} />
                          {PHASE_WORD[phase]} {formatCalendarDate(value) ?? '—'}
                          {formatScheduledTime(value) ? ` · ${formatScheduledTime(value)}` : ''}
                        </span>
                      ))}
                      {job.rig && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {job.rig}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-fieldiq-muted" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
